import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fixedAssetsApi } from '@/services/accountingService';

interface FixedAssetsState {
  fixedAssets: any[];
  fixedAsset: any | null;
  depreciationReport: any | null;
  loading: boolean;
  error: any | null;
}

const initialState: FixedAssetsState = {
  fixedAssets: [],
  fixedAsset: null,
  depreciationReport: null,
  loading: false,
  error: null,
};

// Shared envelope unwrap: every endpoint answers either `{ data: <payload> }`
// or a bare payload, so all thunks must fall back the same way (previously
// only fetchFixedAssets did, and the rest resolved to `undefined`).
const unwrap = (response: any): any => response?.data?.data ?? response?.data ?? null;

/**
 * rr.response.data is a raw server envelope while rr.message is a string,
 * so state.error had an unstable shape that selectors/UI could not render.
 */
const toSafeError = (err: any): string => {
  const payload = err?.response?.data;
  const message =
    (typeof payload === 'string' ? payload : payload?.message) ??
    (typeof err?.message === 'string' ? err.message : '');
  return message && message.length <= 300 ? message : 'An unexpected error occurred.';
};

export const fetchFixedAssets = createAsyncThunk(
  'fixedAssets/fetchFixedAssets',
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      const response: any = await fixedAssetsApi.getAll(params || {});
      return unwrap(response);
    } catch (err: any) {
      return rejectWithValue(toSafeError(err));
    }
  }
);

export const fetchFixedAsset = createAsyncThunk(
  'fixedAssets/fetchFixedAsset',
  async (id: number | string, { rejectWithValue }) => {
    try {
      const response: any = await fixedAssetsApi.getById(id);
      return unwrap(response);
    } catch (err: any) {
      return rejectWithValue(toSafeError(err));
    }
  }
);

export const createFixedAsset = createAsyncThunk(
  'fixedAssets/createFixedAsset',
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await fixedAssetsApi.create(data);
      return unwrap(response);
    } catch (err: any) {
      return rejectWithValue(toSafeError(err));
    }
  }
);

export const updateFixedAsset = createAsyncThunk(
  'fixedAssets/updateFixedAsset',
  async ({ id, data }: { id: number | string; data: any }, { rejectWithValue }) => {
    try {
      const response: any = await fixedAssetsApi.update(id, data);
      return unwrap(response);
    } catch (err: any) {
      return rejectWithValue(toSafeError(err));
    }
  }
);

export const deleteFixedAsset = createAsyncThunk(
  'fixedAssets/deleteFixedAsset',
  async (id: number | string, { rejectWithValue }) => {
    try {
      await fixedAssetsApi.delete(id);
      return id;
    } catch (err: any) {
      return rejectWithValue(toSafeError(err));
    }
  }
);

// NOTE: despite the historical name, this thunk FETCHES the depreciation
// report (fixedAssetsApi.getDepreciationReport) — the result is stored in
// `depreciationReport` so callers can consume it.
export const postDepreciation = createAsyncThunk(
  'fixedAssets/postDepreciation',
  async (data: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      const response: any = await fixedAssetsApi.getDepreciationReport(data || {});
      return unwrap(response);
    } catch (err: any) {
      return rejectWithValue(toSafeError(err));
    }
  }
);

const fixedAssetsSlice = createSlice({
  name: 'fixedAssets',
  initialState,
  reducers: {
    clearFixedAsset(state) {
      state.fixedAsset = null;
    },
    resetFixedAssets(state) {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    const handlePending = (state: FixedAssetsState) => {
      state.loading = true;
      state.error = null;
    };
    const handleRejected = (state: FixedAssetsState, action: any) => {
      state.loading = false;
      state.error = action.payload;
    };

    builder
      .addCase(fetchFixedAssets.pending, handlePending)
      .addCase(fetchFixedAssets.fulfilled, (state, action) => {
        state.loading = false;
        const payload: any = action.payload;
        // Keep the `fixedAssets` array invariant even if the envelope unwraps
        // to a paginated `{ data: [...] }` shape instead of a bare list.
        state.fixedAssets = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : [];
      })
      .addCase(fetchFixedAssets.rejected, handleRejected)

      .addCase(fetchFixedAsset.pending, handlePending)
      .addCase(fetchFixedAsset.fulfilled, (state, action) => {
        state.loading = false;
        state.fixedAsset = action.payload;
      })
      .addCase(fetchFixedAsset.rejected, handleRejected)

      .addCase(createFixedAsset.pending, handlePending)
      .addCase(createFixedAsset.fulfilled, (state, action) => {
        state.loading = false;
        state.fixedAssets.unshift(action.payload);
      })
      .addCase(createFixedAsset.rejected, handleRejected)

      .addCase(updateFixedAsset.pending, handlePending)
      .addCase(updateFixedAsset.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        if (updated?.id == null) return;
        const index = state.fixedAssets.findIndex((f: any) => f.id === updated.id);
        if (index !== -1) {
          state.fixedAssets[index] = updated;
        } else {
          // Not on the current page / filtered out — inserting it keeps the
          // stored copy from silently diverging from the server instead of
          // no-oping.
          state.fixedAssets.unshift(updated);
        }
        if (state.fixedAsset?.id === updated.id) {
          state.fixedAsset = updated;
        }
      })
      .addCase(updateFixedAsset.rejected, handleRejected)

      .addCase(deleteFixedAsset.pending, handlePending)
      .addCase(deleteFixedAsset.fulfilled, (state, action) => {
        state.loading = false;
        // Normalize the id: the thunk accepts `number | string`, so a strict
        // `!==` against numeric list ids silently failed to remove the row.
        const deletedId = String(action.payload);
        state.fixedAssets = state.fixedAssets.filter((f: any) => String(f.id) !== deletedId);
        if (state.fixedAsset != null && String(state.fixedAsset.id) === deletedId) {
          state.fixedAsset = null;
        }
      })
      .addCase(deleteFixedAsset.rejected, handleRejected)

      .addCase(postDepreciation.pending, handlePending)
      .addCase(postDepreciation.fulfilled, (state, action) => {
        state.loading = false;
        state.depreciationReport = action.payload ?? null;
      })
      .addCase(postDepreciation.rejected, handleRejected);
  },
});

export const { clearFixedAsset, resetFixedAssets } = fixedAssetsSlice.actions;

export const selectFixedAssets = (state: any) => state.fixedAssets.fixedAssets;
export const selectFixedAsset = (state: any) => state.fixedAssets.fixedAsset;
export const selectDepreciationReport = (state: any) => state.fixedAssets.depreciationReport;
export const selectFixedAssetsLoading = (state: any) => state.fixedAssets.loading;
export const selectFixedAssetsError = (state: any) => state.fixedAssets.error;

export default fixedAssetsSlice.reducer;
